/**
 * Hooks para operaciones CRUD de Medifarma
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { apiClient } from '@/shared/services/client'
import { API_BASE_URL } from '@/shared/lib/constants'
import type {
  MedifarmaRegistro,
  MedifarmaRegistroCreateInput,
  MedifarmaRegistroUpdateInput,
  PaginatedMedifarmaRegistros,
  MedifarmaStats,
  MedifarmaOpciones,
} from '../types'

// Cliente público sin autenticación para el formulario de registro
const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

const MEDIFARMA_KEYS = {
  all: ['medifarma'] as const,
  lists: () => [...MEDIFARMA_KEYS.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...MEDIFARMA_KEYS.lists(), filters] as const,
  details: () => [...MEDIFARMA_KEYS.all, 'detail'] as const,
  detail: (id: number) => [...MEDIFARMA_KEYS.details(), id] as const,
  stats: () => [...MEDIFARMA_KEYS.all, 'stats'] as const,
  opciones: () => [...MEDIFARMA_KEYS.all, 'opciones'] as const,
}

// ============================================================================
// Queries
// ============================================================================

export function useMedifarmaRegistros(
  page: number = 1,
  pageSize: number = 25,
  filters?: {
    search?: string
    ciudades?: string[]
    estados?: string[]
    farmacia?: string
  }
) {
  return useQuery({
    queryKey: MEDIFARMA_KEYS.list({ page, pageSize, ...filters }),
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      })
      if (filters?.search) params.append('search', filters.search)
      if (filters?.farmacia) params.append('farmacia', filters.farmacia)
      if (filters?.ciudades) {
        filters.ciudades.forEach((ciudad) => params.append('ciudad', ciudad))
      }
      if (filters?.estados) {
        filters.estados.forEach((estado) => params.append('estado', estado))
      }
      const { data } = await apiClient.get<PaginatedMedifarmaRegistros>(
        `/medifarma/?${params}`
      )
      return data
    },
  })
}

export function useMedifarmaRegistro(id: number) {
  return useQuery({
    queryKey: MEDIFARMA_KEYS.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<MedifarmaRegistro>(`/medifarma/${id}/`)
      return data
    },
    enabled: !!id,
  })
}

export function useMedifarmaStats(filters?: {
  ciudades?: string[]
  estados?: string[]
  search?: string
}) {
  return useQuery({
    queryKey: [...MEDIFARMA_KEYS.stats(), filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.search) params.append('search', filters.search)
      if (filters?.ciudades) {
        filters.ciudades.forEach((ciudad) => params.append('ciudad', ciudad))
      }
      if (filters?.estados) {
        filters.estados.forEach((estado) => params.append('estado', estado))
      }
      const queryString = params.toString()
      const { data } = await apiClient.get<MedifarmaStats>(
        `/medifarma/stats/${queryString ? `?${queryString}` : ''}`
      )
      return data
    },
  })
}

export function useMedifarmaOpciones() {
  return useQuery({
    queryKey: MEDIFARMA_KEYS.opciones(),
    queryFn: async () => {
      const { data } = await apiClient.get<MedifarmaOpciones>('/medifarma/opciones/')
      return data
    },
  })
}

// ============================================================================
// Exportar
// ============================================================================

export function useExportarMedifarma() {
  return useMutation({
    mutationFn: async (filters?: {
      search?: string
      ciudades?: string[]
      estados?: string[]
    }) => {
      const params = new URLSearchParams()
      if (filters?.search) params.append('search', filters.search)
      if (filters?.ciudades) {
        filters.ciudades.forEach((ciudad) => params.append('ciudad', ciudad))
      }
      if (filters?.estados) {
        filters.estados.forEach((estado) => params.append('estado', estado))
      }
      const { data } = await apiClient.get(`/medifarma/exportar/`, {
        params,
        responseType: 'blob',
      })
      return data as Blob
    },
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `medifarma_registros_${new Date().toISOString().slice(0, 10)}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    },
  })
}

// ============================================================================
// Mutations
// ============================================================================

export function useCreateMedifarmaRegistro() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: MedifarmaRegistroCreateInput) => {
      const { data } = await apiClient.post<MedifarmaRegistro>('/medifarma/', input)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MEDIFARMA_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: MEDIFARMA_KEYS.stats() })
    },
  })
}

export function useUpdateMedifarmaRegistro() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: MedifarmaRegistroUpdateInput }) => {
      const { data: response } = await apiClient.patch<MedifarmaRegistro>(
        `/medifarma/${id}/`,
        data
      )
      return response
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: MEDIFARMA_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: MEDIFARMA_KEYS.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: MEDIFARMA_KEYS.stats() })
    },
  })
}

export function useDeleteMedifarmaRegistro() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/medifarma/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MEDIFARMA_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: MEDIFARMA_KEYS.stats() })
    },
  })
}

// ============================================================================
// Mutations Públicas (sin autenticación)
// ============================================================================

/**
 * Hook público para crear registros desde el formulario de Medifarma Conecta.
 * No requiere autenticación JWT.
 */
export function useCreateMedifarmaRegistroPublico() {
  return useMutation({
    mutationFn: async (input: Omit<MedifarmaRegistroCreateInput, 'fecha_registro' | 'estado'>) => {
      const { data } = await publicApiClient.post<MedifarmaRegistro>(
        '/medifarma/public/registro/',
        input
      )
      return data
    },
  })
}
